import {Box, ButtonBase, Card, type CardProps} from '@mui/material';
import React from 'react';

type StaticProps = {
  onClick?: undefined;
  actionLabel?: undefined;
  actionDescribedBy?: undefined;
};
type ClickableProps = {
  onClick: () => void;
  /** The button's accessible name, e.g. the station name. */
  actionLabel: string;
  /**
   * Ids of content in the card that describes the button (its
   * aria-describedby), e.g. a status the card shows beside the name. The
   * content sits beside the button, not in it, so it is not in its name.
   */
  actionDescribedBy?: string;
};

export type SurfaceProps = Omit<CardProps, 'onClick'> &
  (StaticProps | ClickableProps);

const ACTION_CLASS = 'ui-Surface-action';

/**
 * The card surface: surface.card, hairline border, no shadow (MuiCard theme
 * override).
 *
 * With `onClick` the card gets a real <button>, stretched over the card
 * BESIDE the content rather than around it: Tab reaches it, Enter and Space
 * activate it (#245), and controls inside the card (a favourite star) stay
 * separate buttons instead of nested interactive content. The content layer
 * lets clicks fall through to the stretched button, except on its own
 * controls.
 *
 * The cost of the fall-through: hover-only children (a Tooltip on a plain
 * <div>) never see the mouse inside a clickable Surface, and its text cannot
 * be selected. Put information in the card as text, not in a hover tooltip.
 */
export const Surface = React.forwardRef<HTMLDivElement, SurfaceProps>(
  ({onClick, actionLabel, actionDescribedBy, children, sx, ...rest}, ref) => {
    if (!onClick) {
      return (
        <Card ref={ref} sx={sx} {...rest}>
          {children}
        </Card>
      );
    }
    return (
      <Card
        ref={ref}
        sx={[
          {
            position: 'relative',
            // The focus ring sits 2px outside the button, i.e. outside the
            // card; hidden overflow would clip it.
            overflow: 'visible',
            cursor: 'pointer',
            // The edge answers the pointer on a card that is a control only
            // (line.control, >= 3:1 on page and card).
            '&:hover': {borderColor: 'border.control'},
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...rest}>
        <ButtonBase
          className={ACTION_CLASS}
          aria-label={actionLabel}
          aria-describedby={actionDescribedBy}
          onClick={onClick}
          disableRipple
          sx={{position: 'absolute', inset: 0, borderRadius: 'inherit'}}
        />
        <Box
          sx={{
            position: 'relative',
            pointerEvents: 'none',
            '& :is(a, button, input, select, textarea, [role="button"], [tabindex])':
              {pointerEvents: 'auto'},
          }}>
          {children}
        </Box>
      </Card>
    );
  },
);
Surface.displayName = 'Surface';
